import React from 'react';
import PropTypes from 'prop-types';
import { anomalyReasonText } from '../../utils/anomalySignals';
import {
    DataTable, GapChip, IconButton, Tooltip,
} from '../ui';
import { formatCurrency } from '../../utils/formatting';
import { getBuyerHeatStatus } from '../../utils/unitDetailHelpers';
import { aiPriceState } from '../../utils/aiPrice';
import {
    SORT_COLUMNS, RISK_TIER_LABELS, unitDisplayName, unitYear,
} from '../../utils/inventoryView';
import { OpenIcon, PlusIcon } from './icons';
import { useDealershipSettings } from '../../contexts/settingsContext';
import { AGING_TONES_BY_ID, unitAgingLabel, unitAgingTone } from '../../utils/dealershipSettings';
import { aiPriceTip } from '../InventoryDashboard/AiPriceCell';
import { DEFAULT_COLUMN_LAYOUT, columnDef, visibleColumnKeys } from '../../utils/inventoryColumns';
import DealStatusChip from './DealStatusChip';
import { NOT_FOR_COMMITTED, isCommittedUnit } from '../../utils/committedUnit';

// Aging tones by bucket id (1 freshest … 4 oldest); a unit's bucket comes
// from the dealership settings (utils/dealershipSettings unitAgingTone).
export const AGING_TONES = AGING_TONES_BY_ID;
const SEVERITY_TONES = { critical: 'crit', warning: 'warn', positive: 'pos' };
const RISK_TONES = { Red: 'red', Yellow: 'amber', Green: 'sage' };
const HEAT_LEVEL = { Hot: 3, Warm: 2, Cold: 1, 'No Interest': 0 };
const HEAT_TIPS = {
    Hot: 'Hot — quoted within last 7 days',
    Warm: 'Warm — quoted within last 30 days',
    Cold: 'Cold — quoted within last 90 days',
    'No Interest': 'No Interest — never quoted or >90 days ago',
};

export const TRUE_COST_TIP = 'Sum of all cost fields for In-Stock units (wholesale + retail + access + after-market + charge + option + other + paint + pre-delivery + repair + surcharge + trim). For Pending units, this is Cost_Estimate (Sales Book Value + planned reconditioning).';
export const RISK_TIP = 'Sell-Speed Risk — calibrated slow-mover (Ladenhüter) risk, 0–100%. Red = high risk of slow sale, Yellow = medium, Green = low. Sort to surface the riskiest units first. Blank = not yet scored.';
export const INTEREST_TIP = 'Buyer interest based on Salesforce JDQuote activity. Hot = quoted in last 7 days. Warm = last 30 days. Cold = last 90 days. No Interest = never quoted or over 90 days ago. Number shown = total quote count.';
export const DEAL_STATUS_TIP = 'Quoted = active quote activity, not a commitment. On hold = held in Salesforce, still in stock; the tooltip gives the reason when one is recorded. Sale pending = marked sold, not yet invoiced. Pre-sold = sold before the unit was received. Sold = invoiced, awaiting close-out. Sorts by precedence: Sold, Pre-sold, Sale pending, On hold, Quoted, Available.';
export const LAST_QUOTE_TIP = 'Gap between the most recent active per-machine quoted price (JDQuoteEquipment) and the current asking price. Negative = buyer offered below ask. Positive = buyer offered above. Within −10% is a retarget signal — call before dropping the listed price.';

// Retarget badge: today's three labels and their tooltips.
export const retargetBadge = (unit) => {
    const viaGap = unit.LastQuotedGapPct != null && unit.LastQuotedGapPct >= -10;
    const viaAI = unit.HasQualifyingQuote;
    if (!viaGap && !viaAI) return null;
    const label = viaGap && viaAI ? 'Gap ≤ 10% · ≥ AI Price' : viaGap ? 'Gap ≤ 10% of ask' : '≥ AI Price';
    const tip = viaGap && viaAI
        ? `Qualifies on both: last quote within 10% of asking price (${unit.LastQuotedGapPct?.toFixed(1)}%) AND at or above AI estimated value`
        : viaGap
            ? `Last quoted price is within 10% of asking price (${unit.LastQuotedGapPct?.toFixed(1)}%) — call before dropping list price`
            : 'Buyer\'s active quote meets or exceeds AI estimated value — asking price may be high but buyer is offering fair market value';
    return { label: `Retarget: ${label}`, tip };
};

// Last quote vs asking: today's colour bands.
export const lastQuoteTone = (gap) => {
    if (gap >= 0) return 'up';
    if (Math.abs(gap) <= 5) return 'near';
    if (Math.abs(gap) <= 10) return 'mid';
    return 'far';
};
export const formatLastQuote = (gap) => `${gap > 0 ? '+' : ''}${gap.toFixed(1)}%`;

// Hours (ODOMETER): "1,234 h", null when unknown.
export const formatHours = (value) => (value == null || Number.isNaN(Number(value))
    ? null
    : `${Math.round(Number(value)).toLocaleString('en-US')} h`);

const DEFAULT_COLUMN_KEYS = visibleColumnKeys(DEFAULT_COLUMN_LAYOUT);

export function RiskPill({ unit }) {
    // A committed unit has a buyer: no sell-speed risk (R2-B1). Short in the
    // grid, the full sentence as its tooltip and for screen readers.
    if (isCommittedUnit(unit)) {
        return (
            <span className="inv2-muted inv2-risk-na" title={NOT_FOR_COMMITTED}>
                <span aria-hidden="true">n/a</span>
                <span className="v2-sr">{NOT_FOR_COMMITTED}</span>
            </span>
        );
    }
    if (!unit.RiskTier) return <span className="inv2-muted">–</span>;
    const label = RISK_TIER_LABELS[unit.RiskTier] || 'Unscored';
    const pct = unit.LadenhueterRisk != null ? unit.LadenhueterRisk.toFixed(0) : null;
    return (
        <span
            className={`inv2-risk inv2-risk--${RISK_TONES[unit.RiskTier] || 'grey'}`}
            title={`Sell-Speed Risk: ${label} (${unit.RiskTier})${pct != null ? ` — ${pct}% slow-mover risk` : ''}`}
        >
            <i aria-hidden="true" />
            {label}
            {pct != null && <span className="inv2-risk-pc">{pct}%</span>}
        </span>
    );
}
RiskPill.propTypes = { unit: PropTypes.object.isRequired };

export function HeatCell({ unit, withCountLabel = false }) {
    const heat = getBuyerHeatStatus(unit.QuoteCount, unit.DaysSinceLastQuote);
    const leads = unit.THEmailLeads30d != null && unit.THEmailLeads30d > 0 ? unit.THEmailLeads30d : 0;
    const tip = leads
        ? `${HEAT_TIPS[heat.label]} · ${leads} marketplace email lead${leads === 1 ? '' : 's'} (last 30d)`
        : HEAT_TIPS[heat.label];
    const level = HEAT_LEVEL[heat.label];
    return (
        <span className={`inv2-heat inv2-heat--${heat.label === 'No Interest' ? 'none' : heat.label.toLowerCase()}`} title={tip}>
            <span className="inv2-bars" aria-hidden="true">
                {[1, 2, 3].map((i) => <span key={i} className={i <= level ? 'on' : undefined} />)}
            </span>
            {heat.label}
            {unit.QuoteCount > 0 && (
                <span className="inv2-qc">{withCountLabel ? `${unit.QuoteCount} quote${unit.QuoteCount === 1 ? '' : 's'}` : unit.QuoteCount}</span>
            )}
            {leads > 0 && <span className="inv2-lead" title={`${leads} marketplace email lead${leads === 1 ? '' : 's'}`} aria-label="marketplace leads" />}
        </span>
    );
}
HeatCell.propTypes = { unit: PropTypes.object.isRequired, withCountLabel: PropTypes.bool };

function EquipmentCell({ unit, retargetActive, catalog }) {
    const badges = [];
    if (unit.NewUsedStatus === 'N') badges.push(<span key="new" className="inv2-tag inv2-tag--new">New</span>);
    if (unit.AnomalyVerdict && unit.AnomalySeverity !== 'normal' && SEVERITY_TONES[unit.AnomalySeverity]) {
        badges.push(
            <span key="v" className={`inv2-tag inv2-tag--${SEVERITY_TONES[unit.AnomalySeverity]}`} title={anomalyReasonText(unit) || unit.AnomalyVerdict}>
                {unit.AnomalyVerdict}
            </span>,
        );
    }
    if (unit.HasQualifyingQuote) {
        badges.push(
            <span
                key="lq"
                className="inv2-tag inv2-tag--lq"
                title={`Open quote at $${unit.MaxOpenQuoteTotal?.toLocaleString('en-US')} meets or exceeds AI estimated price — retarget this customer`}
            >
                Live Quote
            </span>,
        );
    }
    if (retargetActive) {
        const rt = retargetBadge(unit);
        if (rt) badges.push(<span key="rt" className="inv2-tag inv2-tag--lq" title={rt.tip}>{rt.label}</span>);
    }
    const { name, original } = unitDisplayName(unit, catalog);
    return (
        <div className="inv2-eq">
            <div className="inv2-eq-nm">
                <b title={original}>{name}</b>
                {unitYear(unit) && <span>{unitYear(unit)}</span>}
            </div>
            <div className="inv2-eq-sub">
                {unit.CATEGORY && <span className="inv2-cat">{unit.CATEGORY}</span>}
                {badges}
            </div>
        </div>
    );
}
EquipmentCell.propTypes = { unit: PropTypes.object.isRequired, retargetActive: PropTypes.bool, catalog: PropTypes.object };

/**
 * The inventory table (v2). D1 as changed by the user (2026-10-06): a click
 * anywhere in a row (or Space) toggles the preview (I4); the per-cell tabs
 * of v1 live in the preview's tab links. Enter, a double-click and the
 * "Open unit" row action open the unit on Intelligence; arrows move between
 * rows
 * (I6). Hover actions: Open unit and Create task (I5, not in read-only).
 *
 * `columnKeys`: the registry keys (utils/inventoryColumns) to show, in
 * order; the default is today's grid. Each column's DataTable key is its
 * sort key, so `sort` keeps naming unit fields as before.
 */
function InventoryTableV2({
    rows, sort, onSortChange, onOpen, onTogglePreview, selectedStock, retargetActive, readOnly, onCreateTask, machineCatalog,
    columnKeys = DEFAULT_COLUMN_KEYS,
}) {
    const { aging, gapThresholds } = useDealershipSettings();
    const col = (id, label, extra = {}) => {
        const key = columnDef(id).sortKey;
        return {
            id, key, label, sortable: true, defaultDirection: SORT_COLUMNS[key], ...extra,
        };
    };

    const definitions = [
        {
            id: 'aging',
            key: 'aging',
            label: <span className="v2-sr">Aging</span>,
            className: 'inv2-dotc',
            render: (u) => <span className={`inv2-age v2-tone-${unitAgingTone(u, aging)}`} title={unitAgingLabel(u, aging)} />,
        },
        col('stock', 'Stock #', { render: (u) => <span className="inv2-stock">{u.StockNumber}</span> }),
        col('equipment', 'Equipment', { render: (u) => <EquipmentCell unit={u} retargetActive={retargetActive} catalog={machineCatalog} /> }),
        col('branch', 'Branch', { render: (u) => u.BranchName ?? u.LOCATION ?? '—' }),
        col('days', 'Days', {
            align: 'right',
            render: (u) => <span className={`inv2-days inv2-days--${unitAgingTone(u, aging)}`}>{u.DaysInStock == null ? '—' : u.DaysInStock.toLocaleString('en-US')}</span>,
        }),
        col('price', 'Price', {
            align: 'right',
            render: (u) => (u.Best_Sell_Price > 0
                ? <span className="inv2-price">{formatCurrency(u.Best_Sell_Price)}</span>
                : <span className="inv2-muted">—</span>),
        }),
        col('aiPrice', <><span className="inv2-spark" aria-hidden="true" />AI Price</>, {
            align: 'right',
            className: 'inv2-ai-h',
            info: <Tooltip label="About AI Price" content={aiPriceTip(gapThresholds)} />,
            render: (u) => (
                <span className="inv2-ai" data-testid="ai-price-cell" data-state={aiPriceState(u, gapThresholds)}>
                    {aiPriceState(u, gapThresholds) !== 'none' && <span className="inv2-ai-v" aria-hidden="true">{formatCurrency(u.PredictedPrice)}</span>}
                    <GapChip unit={u} />
                </span>
            ),
        }),
        col('trueCost', 'True Cost', {
            align: 'right',
            info: <Tooltip label="About True Cost" content={TRUE_COST_TIP} />,
            render: (u) => (u.CarryingCostBasis != null
                ? <span className="inv2-money">{formatCurrency(u.CarryingCostBasis)}</span>
                : <span className="inv2-muted">—</span>),
        }),
        col('risk', 'Risk', {
            info: <Tooltip label="About Risk" content={RISK_TIP} />,
            render: (u) => <RiskPill unit={u} />,
        }),
        col('interest', 'Interest', {
            info: <Tooltip label="About Interest" content={INTEREST_TIP} />,
            render: (u) => <HeatCell unit={u} />,
        }),
        col('lastQuote', 'Last Quote', {
            align: 'right',
            info: <Tooltip label="About Last Quote" content={LAST_QUOTE_TIP} />,
            render: (u) => (u.LastQuotedGapPct == null
                ? <span className="inv2-muted">—</span>
                : (
                    <span
                        className={`inv2-lq inv2-lq--${lastQuoteTone(u.LastQuotedGapPct)}`}
                        title={`Last per-machine quoted price was ${formatLastQuote(u.LastQuotedGapPct)} vs asking price`}
                    >
                        {formatLastQuote(u.LastQuotedGapPct)}
                    </span>
                )),
        }),
        col('hours', 'Hours', {
            align: 'right',
            render: (u) => {
                const text = formatHours(u.ODOMETER);
                return text ? <span className="inv2-hours">{text}</span> : <span className="inv2-muted">—</span>;
            },
        }),
        col('dealStatus', 'Deal status', {
            className: 'inv2-deal-col',
            info: <Tooltip label="About Deal status" content={DEAL_STATUS_TIP} />,
            render: (u) => <DealStatusChip unit={u} />,
        }),
        {
            id: 'actions',
            key: 'actions',
            label: <span className="v2-sr">Actions</span>,
            className: 'inv2-act',
            render: (u) => (
                <span className="inv2-acts">
                    <IconButton label={`Open unit ${u.StockNumber}`} onClick={() => onOpen(u, 'ai')}>
                        <OpenIcon />
                    </IconButton>
                    {!readOnly && (
                        <IconButton label={`Create task for ${u.StockNumber}`} onClick={() => onCreateTask(u)}>
                            <PlusIcon />
                        </IconButton>
                    )}
                </span>
            ),
        },
    ];
    const byId = Object.fromEntries(definitions.map((d) => [d.id, d]));
    const columns = columnKeys.map((id) => byId[id]).filter(Boolean);

    return (
        <DataTable
            label="Inventory units"
            className="inv2-tscroll"
            columns={columns}
            rows={rows}
            getRowId={(u) => String(u.StockNumber)}
            sort={sort}
            onSortChange={onSortChange}
            onRowClick={onTogglePreview}
            onRowSpace={onTogglePreview}
            // Enter, a double-click and "Open unit" open Intelligence (B7, D1 changed).
            onRowEnter={(unit) => onOpen(unit, 'ai')}
            onRowDoubleClick={(unit) => onOpen(unit, 'ai')}
            selectedId={selectedStock ?? ''}
        />
    );
}

InventoryTableV2.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    sort: PropTypes.shape({ key: PropTypes.string, direction: PropTypes.string }).isRequired,
    onSortChange: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired,
    onTogglePreview: PropTypes.func.isRequired,
    selectedStock: PropTypes.string,
    retargetActive: PropTypes.bool,
    readOnly: PropTypes.bool,
    onCreateTask: PropTypes.func.isRequired,
    machineCatalog: PropTypes.object,
    columnKeys: PropTypes.arrayOf(PropTypes.string),
};

export default InventoryTableV2;
